// Which engine a device gets. A first guess from what the GPU says it is, checked by a short probe of real frames,
// then watched: if frames run slow for a few seconds it drops a tier, and it never climbs back on its own (a tier
// that flips back and forth is worse than either).

export type Tier = 'low' | 'mid' | 'high' | 'ultra';
export const TIERS: Tier[] = ['low', 'mid', 'high', 'ultra'];
export const TIER_LABEL: Record<Tier, string> = { low: 'Low', mid: 'Medium', high: 'High', ultra: 'Ultra' };

export interface TierSpec {
  hdr: boolean;          // float target and post; false draws straight to the screen, tone-mapped in each shader
  msaa: number;
  shadow: number;        // lamp shadow map size, 0 for none
  beams: boolean;
  bloom: boolean;
  grass: number;         // share of liminality's density
  detail: 'low' | 'high';
  mask: number;          // stencil mask size
  dpr: number;           // most device pixels a CSS pixel
  grain: number;
  mirror: boolean;       // the stage floor reflects the gourds
  plain: boolean;        // a pumpkin's light is a plain point light, not thrown through its carving
}

export const SPECS: Record<Tier, TierSpec> = {
  // really basic: no reflections, no shadows, plain lights
  low: { hdr: false, msaa: 0, shadow: 0, beams: false, bloom: false, grass: 0.12, detail: 'low', mask: 1024, dpr: 1.25, grain: 0, mirror: false, plain: true },
  mid: { hdr: true, msaa: 4, shadow: 1024, beams: false, bloom: true, grass: 0.35, detail: 'low', mask: 1024, dpr: 1.5, grain: 0.012, mirror: false, plain: false },
  high: { hdr: true, msaa: 4, shadow: 1024, beams: false, bloom: true, grass: 0.6, detail: 'high', mask: 2048, dpr: 1.5, grain: 0.015, mirror: true, plain: false },
  // everything
  ultra: { hdr: true, msaa: 4, shadow: 2048, beams: true, bloom: true, grass: 1, detail: 'high', mask: 2048, dpr: 2, grain: 0.015, mirror: true, plain: false },
};

export interface Device {
  gpu: string;
  mobile: boolean;
  floatTarget: boolean;
  maxSamples: number;
  maxTexture: number;
  cores: number;
  memory: number;        // GB, 0 where the browser won't say
  screen: number;        // device pixels on the screen, millions
}

export function readDevice(gl: WebGL2RenderingContext, spoof?: string | null): Device {
  const dbg = gl.getExtension('WEBGL_debug_renderer_info');
  const gpu = spoof ?? String(dbg ? gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER));
  const ua = navigator.userAgent;
  const mobile = /Android|iPhone|iPad|iPod|Mobile/i.test(ua) || (navigator.maxTouchPoints > 1 && /Macintosh/.test(ua))
    || matchMedia('(pointer: coarse)').matches;
  return {
    gpu, mobile,
    floatTarget: !!gl.getExtension('EXT_color_buffer_float'),
    maxSamples: gl.getParameter(gl.MAX_SAMPLES) as number,
    maxTexture: gl.getParameter(gl.MAX_TEXTURE_SIZE) as number,
    cores: navigator.hardwareConcurrency || 2,
    memory: (navigator as { deviceMemory?: number }).deviceMemory ?? 0,
    screen: screen.width * screen.height * devicePixelRatio * devicePixelRatio / 1e6,
  };
}

/** the first guess, and why: the reason shows in the settings so a wrong guess can be argued with */
export function guessTier(d: Device): { tier: Tier; why: string } {
  const g = d.gpu.toLowerCase();
  const num = (re: RegExp) => { const m = re.exec(g); return m ? +m[1] : 0; };
  const cap = (t: Tier, why: string) => ({ tier: d.mobile && t === 'ultra' ? 'mid' as Tier : t, why: d.mobile && t === 'ultra' ? why + ', a phone' : why });
  if (!d.floatTarget) return { tier: 'low', why: 'no float render target' };
  if (/swiftshader|llvmpipe|software|basic render|softpipe/.test(g)) return { tier: 'low', why: 'software rendering' };
  if (d.memory && d.memory <= 2) return { tier: 'low', why: `${d.memory} GB memory` };
  if (/adreno/.test(g)) {
    const n = num(/adreno[^0-9]*(\d+)/);
    if (n >= 640) return cap('ultra', `Adreno ${n}`);
    return { tier: n >= 610 ? 'mid' : 'low', why: `Adreno ${n}` };
  }
  if (/mali/.test(g)) {
    const n = num(/mali-g(\d+)/);
    if (/immortalis/.test(g) || n >= 710) return cap('ultra', 'Mali ' + (n || 'Immortalis'));
    return { tier: n >= 72 ? 'mid' : 'low', why: n ? `Mali-G${n}` : 'older Mali' };
  }
  if (/powervr|vivante|videocore/.test(g)) return { tier: 'low', why: 'PowerVR/Vivante/VideoCore' };
  if (/apple m\d|apple gpu|apple/.test(g)) {
    // iOS says only "Apple GPU" whatever the year: let the probe decide between lit and full
    return cap('ultra', d.gpu.replace(/^ANGLE \(|\)$/g, ''));
  }
  if (/nvidia|geforce|rtx|gtx|quadro|radeon|amd/.test(g)) return cap('ultra', 'discrete-class GPU');
  if (/intel/.test(g)) {
    if (/iris|arc|xe/.test(g)) return cap('ultra', 'Intel Iris/Xe/Arc');
    return { tier: 'mid', why: 'Intel integrated' };
  }
  if (d.mobile) return { tier: d.cores >= 8 ? 'mid' : 'low', why: `unknown phone GPU, ${d.cores} cores` };
  return { tier: 'mid', why: 'unknown GPU' };
}

/**
 * Watches frame times. During the probe (the first frames after a tier is set) the median of the last 24 frames
 * decides; after that, the median over a second and a half: strict, under 30 fps it drops a level. Never climbs.
 */
export class Governor {
  private times: number[] = [];
  private since = 0;
  private quietUntil = 0;
  fps = 0;
  constructor(public budgetMs = 30, public onDrop: (why: string) => void = () => {}) {}

  reset(now: number) { this.times = []; this.since = now; this.quietUntil = now + 600; }

  /** one frame's dt; returns true if the tier should drop */
  feed(dt: number, now: number, canDrop: boolean): boolean {
    if (dt > 250) { this.times = []; return false; }   // a hidden tab, a hitch: not a verdict
    if (now < this.quietUntil) return false;            // shaders still compiling, textures uploading
    this.times.push(dt);
    if (this.times.length > 180) this.times.shift();
    const med = median(this.times);
    this.fps = med ? 1000 / med : 0;
    const probing = now - this.since < 4000;
    const enough = probing ? this.times.length >= 24 : this.times.length >= 45;
    if (canDrop && enough && med > this.budgetMs) {
      this.onDrop(`${Math.round(1000 / med)} fps`);
      return true;
    }
    return false;
  }
}

function median(a: number[]): number {
  if (!a.length) return 0;
  const s = [...a].sort((x, y) => x - y);
  return s[s.length >> 1];
}
